import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Users, Calendar, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import { format } from 'date-fns';
import { masterclassesAPI } from '../services/api';
import { useApp } from '../contexts/AppContext';
import './MasterclassDetail.css';

const MasterclassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [masterclass, setMasterclass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(null);

  useEffect(() => {
    fetchMasterclass();
  }, [id]);

  const fetchMasterclass = async () => {
    setLoading(true);
    try {
      const response = await masterclassesAPI.getById(id);
      setMasterclass(response.data);
      // Use helper to safely check registration status from potentially different API structures
      setIsRegistered(response.data.user_registration_status === 'registered' || response.data.is_registered);
      setWaitlistPosition(response.data.user_waitlist_position || response.data.waitlist_position);
    } catch (error) {
      console.error('Failed to fetch masterclass:', error);
      setMasterclass({
        id,
        title: 'AI in Education: Practical Applications',
        description: 'This intensive workshop will explore the practical applications of artificial intelligence in educational settings...',
        instructor: 'Dr. Priya Verma',
        instructor_title: 'AI Research Lead, Google',
        instructor_bio: 'Dr. Verma leads the AI in Education research team at Google...',
        start_time: '2025-01-20T10:00:00',
        end_time: '2025-01-20T12:00:00',
        location: 'Workshop Room A',
        capacity: 30,
        registered: 28,
        prerequisites: 'Basic understanding of educational technology',
        materials: 'Laptop required.',
        is_registered: false,
        waitlist_position: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const response = await masterclassesAPI.register(id);
      if (response.data.success || response.data.status === 'registered') {
        setIsRegistered(true);
        setMasterclass(prev => ({
          ...prev,
          registered: (prev.registered || prev.current_registrations || 0) + 1,
          current_registrations: (prev.registered || prev.current_registrations || 0) + 1
        }));
        showToast('Successfully registered!', 'success');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showToast('Already registered.', 'warning');
      } else if (error.response?.status === 422) {
        showToast('Time conflict with another session', 'error');
      } else {
        showToast('Registration failed. Please try again.', 'error');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleJoinWaitlist = async () => {
    setRegistering(true);
    try {
      const response = await masterclassesAPI.joinWaitlist(id);
      if (response.data.position || response.data.waitlist_position) {
        setWaitlistPosition(response.data.position || response.data.waitlist_position);
        showToast(`Added to waitlist`, 'success');
      }
    } catch (error) {
      showToast('Failed to join waitlist', 'error');
    } finally {
      setRegistering(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your registration?')) return;
    
    setRegistering(true);
    try {
      await masterclassesAPI.cancel(id);
      setIsRegistered(false);
      setWaitlistPosition(null);
      setMasterclass(prev => ({
        ...prev,
        registered: Math.max(0, (prev.registered || prev.current_registrations || 0) - 1),
        current_registrations: Math.max(0, (prev.registered || prev.current_registrations || 0) - 1)
      }));
      showToast('Registration cancelled', 'success');
    } catch (error) {
      showToast('Failed to cancel', 'error');
    } finally {
      setRegistering(false);
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  // === HELPERS FOR API vs DEMO DATA ===
  const getInstructorNames = (instructors) => {
    if (!instructors || instructors.length === 0) return 'TBD';
    return instructors.map(ins => ins.name).join(', ');
  };

  const getAvailability = () => {
    if (!masterclass) return null;
    const registered = masterclass.current_registrations !== undefined 
      ? masterclass.current_registrations 
      : masterclass.registered;
      
    const available = masterclass.capacity - registered;
    if (available <= 0) return { status: 'full', available: 0 };
    if (available <= 5) return { status: 'limited', available };
    return { status: 'available', available };
  };

  if (loading) {
    return (
      <div className="masterclass-detail-page">
        <div className="mc-detail-skeleton">
          <div className="skeleton" style={{ height: 32, width: '80%', marginBottom: 16 }} />
          <div className="skeleton" style={{ height: 20, width: '50%', marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 100, width: '100%' }} />
        </div>
      </div>
    );
  }

  if (!masterclass) {
    return (
      <div className="masterclass-detail-page">
        <div className="empty-state">
          <h3>Masterclass not found</h3>
          <p>This masterclass may have been removed.</p>
        </div>
      </div>
    );
  }

  const availability = getAvailability();
  const instructorNames = getInstructorNames(masterclass.instructors);

  return (
    <div className="masterclass-detail-page">
      <div className="mc-detail-header">
        <h1>{masterclass.title}</h1>
        {isRegistered && (
          <div className="registered-status">
            <CheckCircle size={18} />
            <span>You're registered</span>
          </div>
        )}
        {waitlistPosition && !isRegistered && (
          <div className="waitlist-status">
            <AlertTriangle size={18} />
            <span>Waitlist position: #{waitlistPosition}</span>
          </div>
        )}
      </div>

      <div className="mc-info-grid">
        <div className="info-item">
          <Calendar size={18} />
          <div>
            <span className="info-label">Date</span>
            <span className="info-value">{formatDate(masterclass.start_time)}</span>
          </div>
        </div>
        <div className="info-item">
          <Clock size={18} />
          <div>
            <span className="info-label">Time</span>
            <span className="info-value">{formatTime(masterclass.start_time)} - {formatTime(masterclass.end_time)}</span>
          </div>
        </div>
        <div className="info-item">
          <MapPin size={18} />
          <div>
            <span className="info-label">Location</span>
            <span className="info-value">{masterclass.location}</span>
          </div>
        </div>
        <div className="info-item">
          <Users size={18} />
          <div>
            <span className="info-label">Capacity</span>
            <span className="info-value">
              {masterclass.current_registrations || masterclass.registered}/{masterclass.capacity} 
              {availability?.status === 'full' ? (
                <span className="capacity-full"> (Full)</span>
              ) : (
                <span className="capacity-available"> ({availability?.available} left)</span>
              )}
            </span>
          </div>
        </div>
      </div>

      <section className="mc-section">
        <h2>{masterclass.instructors?.length > 1 ? 'Instructors' : 'Instructor'}</h2>
        <div className="instructors-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {masterclass.instructors && masterclass.instructors.length > 0 ? (
            masterclass.instructors.map((ins) => (
              <div key={ins.id} className="instructor-card">
                <div className="instructor-avatar-lg">
                  {ins.name.charAt(0)}
                </div>
                <div className="instructor-details">
                  <h3>{ins.name}</h3>
                  <p className="title">{ins.title}</p>
                  {ins.bio && (
                    <p className="bio">{ins.bio}</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="instructor-card">
              <div className="instructor-avatar-lg">T</div>
              <div className="instructor-details">
                <h3>To Be Announced</h3>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mc-section">
        <h2>About this Masterclass</h2>
        <div className="mc-description">
          {masterclass.description && masterclass.description.split('\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </section>

      {masterclass.prerequisites && (
        <section className="mc-section">
          <h2>Prerequisites</h2>
          <p>{masterclass.prerequisites}</p>
        </section>
      )}

      {masterclass.materials && (
        <section className="mc-section">
          <h2>What to Bring</h2>
          <p>{masterclass.materials}</p>
        </section>
      )}

      <div className="mc-actions">
        {isRegistered ? (
          <button 
            className="btn btn-outline btn-lg btn-block"
            onClick={handleCancel}
            disabled={registering}
          >
            {registering ? <Loader className="spin" size={20} /> : 'Cancel Registration'}
          </button>
        ) : waitlistPosition ? (
          <button className="btn btn-outline btn-lg btn-block" disabled>
            On Waitlist (#{waitlistPosition})
          </button>
        ) : availability?.status === 'full' ? (
          <button 
            className="btn btn-accent btn-lg btn-block"
            onClick={handleJoinWaitlist}
            disabled={registering}
          >
            {registering ? <Loader className="spin" size={20} /> : 'Join Waitlist'}
          </button>
        ) : (
          <button 
            className="btn btn-primary btn-lg btn-block"
            onClick={handleRegister}
            disabled={registering}
          >
            {registering ? <Loader className="spin" size={20} /> : 'Register Now'}
          </button>
        )}
      </div>
    </div>
  );
};

export default MasterclassDetail;